import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/AuthContext";
import { Lock, Mail, ArrowLeft, ShieldCheck } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    navigate("/admin/dashboard", { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(" "));
      else setError("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8F5] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm text-[#526B57] hover:text-[#7CA63A] transition-colors mb-8"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft size={16} />
          Back to website
        </button>

        <div className="bg-white rounded-2xl border border-[#E3E8DC] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#EAF0E2] rounded-xl flex items-center justify-center">
              <ShieldCheck size={20} className="text-[#7CA63A]" />
            </div>
            <div>
              <h1 className="font-['Outfit'] text-xl font-bold text-[#16231A]">Admin Portal</h1>
              <p className="text-xs text-[#526B57]">Food Safety Partners</p>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#7CA63A] mb-2 block">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#526B57]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-[#F7F8F5] border border-[#E3E8DC] rounded-xl text-sm text-[#16231A] focus:outline-none focus:border-[#7CA63A] focus:ring-1 focus:ring-[#7CA63A] transition-colors"
                  placeholder="sandeep@foodsafetypartners.in"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#7CA63A] mb-2 block">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#526B57]" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-[#F7F8F5] border border-[#E3E8DC] rounded-xl text-sm text-[#16231A] focus:outline-none focus:border-[#7CA63A] focus:ring-1 focus:ring-[#7CA63A] transition-colors"
                  placeholder="Enter your password"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#7CA63A] text-white py-3 rounded-full font-medium hover:bg-[#68902A] transition-colors disabled:opacity-50"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
